import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  UndoOutlined,
  TeamOutlined,
  ShopOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import {
  Table, Button, message, Modal, Input, Space, Form, Select, Tag, Switch,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import usePermissions from "../../../hooks/usePermissions";
import PermissionGuard from "../../../shared/ui/PermissionGuard";
import { ListPage, StatCard, T } from "../../../shared/ui/PageShell";
import { useProductsCatalog } from "../../../hooks/useCatalogData";
import { parseLocalizedName } from "../../../utils/localization";
import { fixedStart } from "../../../i18";
import useTablePagination from "../../../shared/ui/useTablePagination";

const GetUsers = () => {
  const { t, i18n } = useTranslation();
  const { pagination } = useTablePagination();
  const { hasPermission } = usePermissions();
  const { data: allProducts } = useProductsCatalog();
  const [productModalUser, setProductModalUser] = useState(null);
  const [allowedIds, setAllowedIds] = useState([]);
  const [productSaving, setProductSaving] = useState(false);
  const [visibilityModalUser, setVisibilityModalUser] = useState(null);
  const [hiddenFromIds, setHiddenFromIds] = useState([]);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/users", { params: { include_archived: includeArchived ? 1 : 0 } });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch {
      message.error(t("usersLoadError"));
    } finally {
      setLoading(false);
    }
  }, [includeArchived, t]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await apiClient.get("/roles");
      setRoles(res.data.roles || []);
    } catch { setRoles([]); }
  }, []);

  useEffect(() => { fetchUsers(); fetchRoles(); }, [fetchUsers, fetchRoles]);

  const roleOptions = useMemo(() =>
    roles.length > 0
      ? roles.map((r) => ({ value: r.name, label: r.display_name }))
      : [
        { value: "admin", label: t("roles.admin", { defaultValue: "Admin" }) },
        { value: "user", label: t("roles.user", { defaultValue: "User" }) },
      ],
    [roles, t]
  );

  const filteredUsers = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      String(u.name || "").toLowerCase().includes(term) ||
      String(u.email || "").toLowerCase().includes(term) ||
      String(u.phone || "").toLowerCase().includes(term) ||
      String(u.role || "").toLowerCase().includes(term)
    );
  }, [users, searchText]);

  const openCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: "user" });
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      phone: user.phone || "",
      role: user.role,
      password: "",
      shift_start: user.shift_start || "",
      shift_end: user.shift_end || "",
      hide_from_non_admins: user.hide_from_non_admins !== false,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const isAdminRole = String(values.role || "").toLowerCase() === "admin";
      const start = values.shift_start || null;
      const end = values.shift_end || null;
      const payload = {
        name: values.name,
        email: values.email || null,
        phone: String(values.phone || "").trim(),
        role: values.role,
        // Admins are never shift-locked; blank either side = no limit.
        shift_start: isAdminRole ? null : (start && end ? start : null),
        shift_end: isAdminRole ? null : (start && end ? end : null),
      };
      if (isAdminRole) {
        payload.hide_from_non_admins = values.hide_from_non_admins !== false;
      }
      if (values.password) payload.password = values.password;
      setSaving(true);
      if (editingUser) {
        await apiClient.put(`/users/${editingUser.id}`, payload);
        message.success(t("userUpdated"));
      } else {
        if (!payload.password) { message.error(t("userPasswordRequired")); return; }
        await apiClient.post("/users", payload);
        message.success(t("userCreated"));
      }
      setModalOpen(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(t("userSaveError"));
    } finally { setSaving(false); }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      await apiClient.delete(`/users/${archiveTarget.id}`);
      message.success(t("userArchived"));
      setArchiveTarget(null);
      fetchUsers();
    } catch { message.error(t("userArchiveError")); }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    try {
      await apiClient.put(`/users/${restoreTarget.id}/restore`);
      message.success(t("userRestored"));
      setRestoreTarget(null);
      fetchUsers();
    } catch { message.error(t("userRestoreError")); }
  };

  const getRoleDisplay = (roleName) => {
    const roleObj = roles.find((r) => r.name === roleName);
    return roleObj?.display_name || roleName;
  };

  const productOptions = useMemo(
    () => (allProducts || []).map((p) => ({ value: p.id, label: parseLocalizedName(p.name, i18n.language, `#${p.id}`) })),
    [allProducts, i18n.language]
  );

  const openProductModal = async (user) => {
    setProductModalUser(user);
    setAllowedIds([]);
    try {
      const r = await apiClient.get(`/users/${user.id}/sellable-products`);
      setAllowedIds(r.data?.product_ids || []);
    } catch { setAllowedIds([]); }
  };

  const saveProducts = async () => {
    if (!productModalUser) return;
    setProductSaving(true);
    try {
      await apiClient.put(`/users/${productModalUser.id}/sellable-products`, { product_ids: allowedIds });
      message.success(t("sellableProductsSaved", { defaultValue: "Sellable products updated" }));
      setProductModalUser(null);
    } catch {
      message.error(t("userSaveError"));
    } finally {
      setProductSaving(false);
    }
  };

  const openVisibilityModal = async (user) => {
    setVisibilityModalUser(user);
    setHiddenFromIds([]);
    try {
      const r = await apiClient.get(`/users/${user.id}/hidden-from`);
      setHiddenFromIds(r.data?.viewer_ids || []);
    } catch { setHiddenFromIds([]); }
  };

  const saveVisibility = async () => {
    if (!visibilityModalUser) return;
    setVisibilitySaving(true);
    try {
      await apiClient.put(`/users/${visibilityModalUser.id}/hidden-from`, { viewer_ids: hiddenFromIds });
      message.success(t("userHiddenFromSaved", { defaultValue: "Visibility updated" }));
      setVisibilityModalUser(null);
    } catch {
      message.error(t("userSaveError"));
    } finally {
      setVisibilitySaving(false);
    }
  };

  const activeCount = users.filter((u) => !u.deleted_at).length;

  const columns = [
    {
      title: <b>{t("actions")}</b>,
      key: "actions",
      fixed: fixedStart(i18n.language),
      render: (_, record) => (
        <Space>
          {hasPermission("users.edit") && (
            <Button type="primary" size="small" icon={<EditOutlined />} style={{ borderRadius: 7 }} onClick={() => openEditModal(record)} disabled={Boolean(record.deleted_at)} />
          )}
          {hasPermission("users.edit") && (
            <Button size="small" icon={<ShopOutlined />} style={{ borderRadius: 7 }} onClick={() => openProductModal(record)} disabled={Boolean(record.deleted_at)} title={t("sellableProducts", { defaultValue: "Sellable products" })} />
          )}
          {hasPermission("users.edit") && (
            <Button size="small" icon={<EyeInvisibleOutlined />} style={{ borderRadius: 7 }} onClick={() => openVisibilityModal(record)} disabled={Boolean(record.deleted_at)} title={t("userHiddenFrom", { defaultValue: "Hide from users" })} />
          )}
          {record.deleted_at
            ? hasPermission("users.edit") && (
              <Button size="small" icon={<UndoOutlined />} style={{ borderRadius: 7 }} onClick={() => setRestoreTarget(record)} />
            )
            : hasPermission("users.delete") && (
              <Button danger size="small" icon={<DeleteOutlined />} style={{ borderRadius: 7 }} onClick={() => setArchiveTarget(record)} />
            )}
        </Space>
      ),
    },
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => <span style={{ color: T.muted, fontSize: 13 }}>{index + 1}</span>,
    },
    {
      title: <b>{t("name")}</b>,
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 600, color: T.text }}>{text}</span>,
    },
    {
      title: <b>{t("phone")}</b>,
      dataIndex: "phone",
      key: "phone",
      render: (text) => <span style={{ color: T.sub }}>{text || "—"}</span>,
    },
    {
      title: <b>{t("email")}</b>,
      dataIndex: "email",
      key: "email",
      render: (text) => <span style={{ color: T.sub }}>{text || "—"}</span>,
    },
    {
      title: <b>{t("role")}</b>,
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag color={role === "admin" ? "blue" : "default"} style={{ borderRadius: 20 }}>
          {getRoleDisplay(role)}
        </Tag>
      ),
    },
    {
      title: <b>{t("status")}</b>,
      key: "status",
      render: (_, record) =>
        record.deleted_at ? (
          <span style={{ background: "#fee2e2", color: T.red, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{t("archived")}</span>
        ) : (
          <span style={{ background: "#dcfce7", color: T.green, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{t("active")}</span>
        ),
    },
  ];

  const stats = [
    <StatCard key="total" icon={<TeamOutlined style={{ color: "#2563eb" }} />} label={t("users")} value={users.length} iconBg="#dbeafe" />,
    <StatCard key="active" icon={<UserAddOutlined style={{ color: "#16a34a" }} />} label={t("active")} value={activeCount} iconBg="#dcfce7" valueColor={T.green} />,
  ];

  const toolbar = (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <Input.Search
        placeholder={t("searchPlaceholder")}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        allowClear
        style={{ width: 280, borderRadius: 9 }}
      />
      <Space style={{ marginLeft: "auto" }}>
        <span style={{ color: T.sub, fontSize: 13 }}>{t("showArchived")}</span>
        <Switch checked={includeArchived} onChange={setIncludeArchived} />
        <PermissionGuard permission="users.add" hide>
          <Button type="primary" icon={<UserAddOutlined />} onClick={openCreateModal} style={{ borderRadius: 9, fontWeight: 600 }}>
            {t("addUser")}
          </Button>
        </PermissionGuard>
      </Space>
    </div>
  );

  return (
    <>
      <ListPage
        breadcrumb={[t("navigation.administration", { defaultValue: "Administration" }), t("users")]}
        title={t("users")}
        subtitle={`${activeCount} ${t("active").toLowerCase()}`}
        stats={stats}
        toolbar={toolbar}
      >
        <Table
          loading={loading}
          dataSource={filteredUsers.map((item) => ({ ...item, key: item.id }))}
          columns={columns}
          pagination={pagination}
          scroll={{ x: 800 }}
        />
      </ListPage>

      <Modal title={editingUser ? t("editUser") : t("addUser")} open={modalOpen} onOk={handleSave} okText={editingUser ? t("edit") : t("addUser")} confirmLoading={saving} onCancel={() => { setModalOpen(false); setEditingUser(null); form.resetFields(); }} cancelText={t("cancel")} centered>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label={t("name")} rules={[{ required: true, message: t("enterName") }]}><Input /></Form.Item>
          <Form.Item
            name="phone"
            label={t("phone")}
            rules={[
              { required: true, message: t("enterPhone") },
              {
                pattern: /^[+]?[\d\s()-]{7,20}$/,
                message: t("invalidPhone", "Enter a valid phone number"),
              },
            ]}
          >
            <Input placeholder={t("enterPhone")} inputMode="tel" autoComplete="tel" />
          </Form.Item>
          <Form.Item name="email" label={t("email")} rules={[{ type: "email", message: t("invalidEmail") }]}><Input placeholder={t("enterEmail")} /></Form.Item>
          <Form.Item name="role" label={t("role")} rules={[{ required: true, message: t("selectRole") }]}><Select options={roleOptions} /></Form.Item>
          <Form.Item shouldUpdate={(prev, cur) => prev.role !== cur.role} noStyle>
            {({ getFieldValue }) => String(getFieldValue("role") || "").toLowerCase() === "admin" ? (
              <Form.Item
                name="hide_from_non_admins"
                label={t("hideFromNonAdminsLabel", { defaultValue: "Hide this admin from non-admin users" })}
                tooltip={t("hideFromNonAdminsHint", { defaultValue: "When on (default), this admin does not appear in any list/filter/picker seen by non-admin users. Other admins always see everyone regardless of this setting." })}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch />
              </Form.Item>
            ) : null}
          </Form.Item>
          <Form.Item name="password" label={t("password")} rules={editingUser ? [{ min: 6, message: t("passwordMin") }] : [{ required: true, message: t("userPasswordRequired") }, { min: 6, message: t("passwordMin") }]}><Input.Password placeholder={editingUser ? t("leaveBlankKeepPassword") : ""} /></Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item name="shift_start" label={t("shiftStart", "Working hours — start")} tooltip={t("shiftHint", "Leave both empty for no limit. When set, outside these hours the user cannot sell, return, edit, cancel, or delete (view and logout only).")}>
              <Select allowClear showSearch placeholder="--:--" options={Array.from({ length: 48 }, (_, i) => { const h = String(Math.floor(i / 2)).padStart(2, "0"); const m = i % 2 ? "30" : "00"; return { value: `${h}:${m}`, label: `${h}:${m}` }; })} />
            </Form.Item>
            <Form.Item name="shift_end" label={t("shiftEnd", "Working hours — end")}>
              <Select allowClear showSearch placeholder="--:--" options={Array.from({ length: 48 }, (_, i) => { const h = String(Math.floor(i / 2)).padStart(2, "0"); const m = i % 2 ? "30" : "00"; return { value: `${h}:${m}`, label: `${h}:${m}` }; })} />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <Modal title={t("confirmArchive")} open={Boolean(archiveTarget)} onOk={handleArchive} onCancel={() => setArchiveTarget(null)} okText={t("archive")} okButtonProps={{ danger: true }} cancelText={t("cancel")} centered>
        <p>{t("archiveUserConfirm")}</p>
      </Modal>

      <Modal title={t("confirmRestore")} open={Boolean(restoreTarget)} onOk={handleRestore} onCancel={() => setRestoreTarget(null)} okText={t("restore")} cancelText={t("cancel")} centered>
        <p>{t("restoreUserConfirm")}</p>
      </Modal>

      <Modal
        title={`${t("sellableProducts", { defaultValue: "Sellable products" })}${productModalUser ? ` — ${productModalUser.name}` : ""}`}
        open={Boolean(productModalUser)}
        onOk={saveProducts}
        confirmLoading={productSaving}
        onCancel={() => setProductModalUser(null)}
        okText={t("save", { defaultValue: "Save" })}
        cancelText={t("cancel")}
        centered
        width={560}
      >
        <p style={{ color: T.sub, fontSize: 13, marginBottom: 10 }}>
          {t("sellableProductsHint", {
            defaultValue: "Choose the products this cashier can sell. Leave empty to allow all products.",
          })}
        </p>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t("allProductsAllowed", { defaultValue: "All products (no restriction)" })}
          value={allowedIds}
          onChange={setAllowedIds}
          options={productOptions}
          style={{ width: "100%" }}
          maxTagCount="responsive"
        />
        <div style={{ marginTop: 10, fontSize: 12, color: T.muted }}>
          {allowedIds.length === 0
            ? t("allProductsAllowed", { defaultValue: "All products (no restriction)" })
            : t("products.count", { defaultValue: "{{count}} products", count: allowedIds.length })}
        </div>
      </Modal>

      <Modal
        title={`${t("userHiddenFrom", { defaultValue: "Hide from users" })}${visibilityModalUser ? ` — ${visibilityModalUser.name}` : ""}`}
        open={Boolean(visibilityModalUser)}
        onOk={saveVisibility}
        confirmLoading={visibilitySaving}
        onCancel={() => setVisibilityModalUser(null)}
        okText={t("save", { defaultValue: "Save" })}
        cancelText={t("cancel")}
        centered
        width={560}
      >
        <p style={{ color: T.sub, fontSize: 13, marginBottom: 10 }}>
          {t("userHiddenFromHint", {
            defaultValue: "Choose which users cannot see this user in any list, filter, or picker across the system. Their past sales/orders remain fully visible. Leave empty to allow everyone to see them.",
          })}
        </p>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t("userHiddenFromNone", { defaultValue: "Visible to everyone" })}
          value={hiddenFromIds}
          onChange={setHiddenFromIds}
          options={users
            .filter((u) => u.id !== visibilityModalUser?.id && !u.deleted_at)
            .map((u) => ({ value: u.id, label: u.name }))}
          style={{ width: "100%" }}
          maxTagCount="responsive"
        />
        <div style={{ marginTop: 10, fontSize: 12, color: T.muted }}>
          {hiddenFromIds.length === 0
            ? t("userHiddenFromNone", { defaultValue: "Visible to everyone" })
            : t("userHiddenFromCount", { defaultValue: "Hidden from {{count}} user(s)", count: hiddenFromIds.length })}
        </div>
      </Modal>
    </>
  );
};

export default GetUsers;
